<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import * as Sentry from "@sentry/vue";
import { ElNotification, type FormInstance } from "element-plus";
import { C } from "jssip";
import useDetectOutsideClick from "~/composable/useDetectOutsideClick.js";
import { useSystemThemeSync } from "~/composable/useSystemThemeSync";
import { useAuthStore } from "~/stores/auth";
import { ClientStatus } from "~/types/enums/ClientStatus";
import { InCall } from "~/types/enums/InCall";
import { useAgentSipConfigStore } from "~/stores/agentSipConfigStore";
import CallService from "~/projects/cc/services/CallService";
import { init as initAudioService } from "~/projects/cc/services/AudioService";
import { type ThemeMode, useThemeStore } from "~/stores/themeStore";
import DesktopIntegration from "~/services/DesktopIntegration";
import IFrameIntegration from "~/services/IFrameIntegration";
import { useTasksStore } from "~/projects/task-manager/stores/tasksStore";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";
import { EventService } from "~/projects/help-desk/events/eventService";
import { PermissionNames } from "~/configs/permission_names";
import { isUserPermitted } from "~/utils/permissionHelper";
import { useTopBarBadgeStore } from "~/stores/topBarBadgeStore";
import { ConversationRead } from "~/projects/help-desk/types/enums/ConversationRead";
import { useOpportunitiesStore } from "~/projects/crm/stores/opportunitiesStore";
import OpportunitiesCreateForm from "~/projects/crm/components/opportunities/CreateForm.vue";
import shortcutHandler from "~/projects/cc/utils/shortcutHandler";
import { Task } from "~/projects/task-manager/types/task";
import { useTicketsStore } from "~/projects/help-desk/stores/ticketsStore";
import { Conversation, Ticket } from "~/projects/help-desk/types";
import { useConfigStore } from "~/stores/configStore";
import { useGlobalComponentStore } from "~/stores/globalComponentStore";
import { useNotificationStore } from "~/stores/notificationStore";
import { Notification } from "~/types/notification";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { formatDuration } from "~/projects/cc/utils/callCenterHelper"; // Import formatDuration
import { useOrdersStore } from "~/projects/order-manager/stores/ordersStore";
import { useSupportStore } from "~/stores/supportStore";
import { useLeadsStore } from "~/projects/crm/stores/leadsStore";
import LeadsCreateForm from "~/projects/crm/components/leads/CreateForm.vue";
const router = useRouter();
const ordersStore = useOrdersStore();
const globalComponentStore = useGlobalComponentStore();
const configStore = useConfigStore();
const ticketsStore = useTicketsStore();
const opportunitiesStore = useOpportunitiesStore();
const leadsStore = useLeadsStore();
const profileStore = useCustomerProfileStore();
const tasksStore = useTasksStore();
const topBarBadgeStore = useTopBarBadgeStore();
const dialogStore = useDialogStore();
const notificationStore = useNotificationStore();
const supportStore = useSupportStore();

let callService: CallService;
const authStore = useAuthStore();

// Today's follow-ups live behind the CRM feature and the plain lead-show
// permission — the endpoint only ever returns the caller's own follow-ups.
const canSeeTodayFollowUps = computed(
  () => authStore.features.crm && isUserPermitted(PermissionNames.LEAD_SHOW),
);
const openTodayFollowUps = () => {
  leadsStore.setTodayFollowUpsModalVisibility(true);
};
let eventService: EventService;
const formRef = ref<FormInstance | null>(null);
let supportTokenBootstrapTimer: ReturnType<typeof setTimeout> | null = null;
let badgeCountTimer: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
  if (configStore._globalConfig?.client?.status === ClientStatus.INACTIVE) {
    authStore._clientStatus = configStore._globalConfig?.client?.status;
    authStore._clientMessage = configStore._globalConfig?.client?.message || "";
    router.push("/payment-required");
  }
  // Handlers go on before the first await. mitt drops an event with no listener
  // attached, so registering after the badge seed left a window in which an
  // assignment or removal delivered mid-request vanished. Together with the
  // seed generation guard in topBarBadgeStore, an event that arrives while the
  // seed is in flight is now recorded and replayed on top of the snapshot
  // instead of being lost to it.
  initializeEventService();
  await authStore.getClientInfo();
  // The ping keeps its place at the head of the mount — it drives the agent's
  // presence and its own 60s interval, and neither should wait on a badge
  // request. It only needs to stop taking the rest of the mount down with it:
  // it dereferences getClientInfo()'s result, which is null on a failed
  // request, and that one throw used to cost the badge both its seed and its
  // timer for the whole session. Reported, not swallowed.
  try {
    await postRepeatedActivePing();
  } catch (error) {
    Sentry.captureException(error);
  }
  await topBarBadgeStore.getTopBarBadgeCount();
  // One cheap request keeps every top-bar number honest. The badges are seeded
  // once at mount and then only nudged by socket events, so without this a
  // count that changed elsewhere — a follow-up assigned to this agent by
  // someone else, an event missed while the socket was down, a shift that runs
  // past midnight — stays wrong until the next full page load. It is also the
  // only recovery path for an event the socket never delivered, which is what
  // the whole push/pop design leans on, so nothing ahead of it may throw.
  badgeCountTimer = setInterval(
    () => topBarBadgeStore.getTopBarBadgeCount(),
    5 * 60 * 1000,
  );
  supportTokenBootstrapTimer = setTimeout(() => {
    supportStore.getToken().catch(() => null);
  }, 5000);
  document.addEventListener("keydown", shortcutHandler);

  const userPrefix =
    new URL(useRuntimeConfig().public.APP_BASE_URL).hostname.split(".")?.[0] +
    "_";
  Sentry.setUser({ username: userPrefix + authStore.user.user_name });
});

onUnmounted(() => {
  if (supportTokenBootstrapTimer) {
    clearTimeout(supportTokenBootstrapTimer);
    supportTokenBootstrapTimer = null;
  }
  if (badgeCountTimer) {
    clearInterval(badgeCountTimer);
    badgeCountTimer = null;
  }
  document.removeEventListener("keydown", shortcutHandler);
  destroyEventService();
});

const initializeEventService = function () {
  const { $listen } = useNuxtApp();
  $listen("ticket_created", handleTicketGenerated);
  $listen("ticket_unpicked", handleTicketGenerated);
  $listen("ticket_assigned", handleTicketAssigned);
  $listen("uh_call_created", handleUhCallCreated);
  $listen("uh_calls_handled", handleUhCallsHandled);
  $listen("task_created", handleTaskCreated);
  $listen("task_reminder", handleTaskReminder);
  $listen("conv_created", handleMessageReceived);
  $listen("notification_created", handleNotificationCreated);
  $listen("follow_up_assigned", handleFollowUpAssigned);
  $listen("follow_up_removed", handleFollowUpRemoved);
};

const destroyEventService = function () {
  const { $unListen } = useNuxtApp();
  $unListen("ticket_created", handleTicketGenerated);
  $unListen("ticket_unpicked", handleTicketGenerated);
  $unListen("ticket_assigned", handleTicketAssigned);
  $unListen("uh_call_created", handleUhCallCreated);
  $unListen("uh_calls_handled", handleUhCallsHandled);
  $unListen("task_created", handleTaskCreated);
  $unListen("task_reminder", handleTaskReminder);
  $unListen("conv_created", handleMessageReceived);
  $unListen("notification_created", handleNotificationCreated);
  $unListen("follow_up_assigned", handleFollowUpAssigned);
  $unListen("follow_up_removed", handleFollowUpRemoved);
};

const handleTicketGenerated = (ticket: Ticket): void => {
  if (ticketsStore.isTicketForMe(ticket) && ticket.status !== 7) {
    topBarBadgeStore.incrementTicketCount();
    if (useAudioSettingStore().user.newTicket.active) {
      if (ticket.owner?.name) {
        ElNotification({
          title: "New Ticket",
          message: `New Ticket from ${ticket.owner?.name} !!!`,
          type: "success",
        });
      } else {
        ElNotification({
          title: "New Ticket",
          message: `New Ticket !!!`,
          type: "success",
        });
      }
    }
  }
};

const handleTicketAssigned = (ticket: Ticket) => {
  if (!ticket.auto_pick) {
    topBarBadgeStore.decrementTicketCount();
  }
};

const handleUhCallCreated = () => {
  topBarBadgeStore.adjustUnhandledCallsCount(1);
};

const handleUhCallsHandled = (e) => {
  topBarBadgeStore.adjustUnhandledCallsCount(-e.length);
};

const handleTaskCreated = (task: Task) => {
  ElNotification({
    title: "New Task",
    message: `Task '${task.title}' is assigned to you`,
    type: "info",
  });
};

const handleTaskReminder = (task: Task) => {
  topBarBadgeStore.incrementOverdueTaskCount();
  ElNotification({
    title: "Task Reminder",
    message: `Task '${task.title}' is due ${task.due_time}.`,
    type: "warning",
  });
};

// A follow-up became due-today-and-open for this agent. Deliberately silent:
// the agent already gets a popup for this out of the bell pipeline, where the
// backend writes a Notification row that comes back as notification_created and
// handleNotificationCreated below turns into an ElNotification. Adding a toast
// here would show the same event twice.
const handleFollowUpAssigned = (followUp: any) => {
  if (!followUp?.id) {
    return;
  }
  topBarBadgeStore.addTodayFollowUpId(followUp.id);
  leadsStore.upsertTodayFollowUp(followUp);
};

// The follow-up stopped qualifying — closed, moved off today, reassigned to
// someone else or deleted. The badge and the modal both have to let go of it.
const handleFollowUpRemoved = (payload: { id: number }) => {
  if (!payload?.id) {
    return;
  }
  topBarBadgeStore.removeTodayFollowUpId(payload.id);
  leadsStore.removeTodayFollowUp(payload.id);
};

const user = authStore.user;
const handleMessageReceived = function (conversation: Conversation) {
  if (conversation.ticket.agent_id !== user.id) {
    return;
  }
  topBarBadgeStore.adjustMessageCount(1);
};

const handleNotificationCreated = (notification: Notification) => {
  if (notificationStore.notifications.length > 0) {
    notificationStore.notifications.unshift(notification);
  }
  topBarBadgeStore.incrementUnReadNotificationCount();
  ElNotification({
    title: "New Notification",
    message: notification.body,
    type: "info",
  });
};

const dialerNumber = ref("");

const numberQuery = useRoute().query.number as string;
if (numberQuery) {
  dialerNumber.value = numberQuery;
  dialogStore.setDialerVisibility(true);
}

// const dialogStore = useDialogStore();

const theme = useThemeStore();

// Follow the operating system for as long as the app is open. The store reads
// the OS once, when it is created, so the first paint is already right; this
// adds the live half, for a machine that switches to dark while someone works.
useSystemThemeSync();

/*
 * The three theme settings, in the order the dropdown draws them. Each icon is
 * a single heroicons outline path, so one <svg> inside the loop can draw all
 * three rather than three near-identical blocks behind v-ifs.
 */
const themeOptions: { value: ThemeMode; label: string; icon: string }[] = [
  {
    value: "light",
    label: "Light",
    icon: "M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z",
  },
  {
    value: "dark",
    label: "Dark",
    icon: "M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z",
  },
  {
    value: "system",
    label: "System",
    icon: "M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25",
  },
];

/*
 * The profile dropdown's identity block. `authStore.user` is initialised to an
 * empty object and is only filled once the session loads, so every field is
 * treated as absent until proven otherwise — binding the email straight into
 * the template prints "undefined" under the name on a cold load.
 */
const profileName = computed(() => authStore.user?.name?.trim() || "");
const profileEmail = computed(() => authStore.user?.email?.trim() || "");

// First and last initial — the same two letters the comment avatars draw.
const profileInitials = computed(() => {
  const parts = profileName.value.split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
});

// Client notice from the backend, shown inline in the top bar between the
// title and the right-side actions. The message may contain an <a> tag, so
// it is rendered with v-html.
const clientMessage = computed(() => authStore.clientMessage?.trim() || "");
// Two render targets: inline in the top bar (sm+) and a bar under the header
// (mobile). Both need their anchors post-processed.
const clientMessageRef = ref<HTMLElement | null>(null);
const clientMessageMobileRef = ref<HTMLElement | null>(null);

// Open any link from the message in a new tab so clicking a promo link never
// drops the agent's active call-center session. A `target` set explicitly by
// the backend is respected.
watch(
  clientMessage,
  async (value) => {
    if (!value) return;
    await nextTick();
    [clientMessageRef.value, clientMessageMobileRef.value].forEach((el) => {
      el?.querySelectorAll("a").forEach((anchor: HTMLAnchorElement) => {
        if (!anchor.getAttribute("target"))
          anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
      });
    });
  },
  { immediate: true },
);

// newTab is used to show the newTab dropdown in the top navbar
const newTab = ref(false);
// profileTab is used to show the newTab dropdown in the top navbar
const profileTab = ref(false);

// newTabButton and profileTabButton are used to show the newTab and profileTab dropdown repectively when they are clicked.
const newTabButton = ref(null);
const profileTabButton = ref(null);
// useDetectOutsideClick function is used to detect any clicks outside the reffered elem and executes the call function

useDetectOutsideClick(profileTabButton, () => {
  profileTab.value = false;
});

useDetectOutsideClick(newTabButton, () => {
  newTab.value = false;
});

const desktopIntegration = new DesktopIntegration();
initAudioService();
provide("desktopIntegration", desktopIntegration);

const iFrameIntegration = new IFrameIntegration();

if (authStore.features.call_center) {
  callService = new CallService();
  if (numberQuery) {
    callService.init(true);
  } else {
    callService.init();
  }
  iFrameIntegration.init(callService);
  desktopIntegration.init(callService);
  provide("callService", callService);
}

eventService = new EventService();
provide("eventService", eventService);

computed(() => {
  if (theme.getTheme) {
    return "#107297";
  }
  return "#3B2A82";
});
const bodyClass = computed(() => {
  if (theme.getTheme) {
    return "dark";
  }
  return "";
});

const handleConnectionClick = () => {
  if (useAgentSipConfigStore().connectionStatus === "connected_elsewhere") {
    callService.init(true);
    iFrameIntegration.init(callService);
    desktopIntegration.init(callService);
  }
};

const getConnectionStatusClass = function (status: string) {
  const grayBg = "text-gray-400 hover:text-gray-300 transition-all";
  const greenBg = "text-green-400 hover:text-green-300 transition-all";
  const yellowBg = "text-yellow-400 hover:text-yellow-300 transition-all";
  const redBg = "text-red-500 hover:text-red-400 transition-all";

  if (status === "connected") return greenBg;
  else if (status === "connected_elsewhere") return yellowBg;
  else if (status === "disconnected") return grayBg;
  return redBg;
};

const getUserStatusClass = function (status: string) {
  const greenBg =
    " bg-green-600 shadow-green-600  hover:bg-green-500 hover:text-white transition-all";
  const redBg =
    " bg-red-600 shadow-red-600  hover:bg-red-500 hover:text-white transition-all";

  return status === "online" ? greenBg : redBg;
};

const changeStatus = async function () {
  const status = authStore.user.status === "online" ? "offline" : "online";
  await authStore.changeStatus(status);
};

const handleCommand = async (command: string) => {
  if (command !== "online") {
    await authStore.changeStatus("offline", command);
  } else {
    await authStore.changeStatus(command);
  }
};

const signOut = function () {
  authStore.logout();
  authStore.$reset();
  // Support holds its own token, and it outlives a session unless it is told.
  supportStore.clearSession();
  callService?.endConnection();
  useRouter().push("/");
};

useHead({
  bodyAttrs: {
    class: bodyClass,
  },
});

const rightNav = [
  {
    name: "Unhandled Calls",
    img: "/svgs/sidebar/unhandled-calls.svg",
    to: "call-center/unhandled-calls",
    number: computed(() => topBarBadgeStore.unhandledCallsCount),
    permissionName: PermissionNames.UNHANDLED_CALL_HISTORY_SHOW,
  },
  {
    name: "New Tickets",
    img: "/images/new_tickets.png",
    to: "help-desk/ticket-list",
    number: computed(() => topBarBadgeStore.newTicketsCount),
    permissionName: PermissionNames.TICKET_SHOW,
  },
  {
    name: "New Messages",
    img: "/images/new_messages.png",
    to: "help-desk/inbox",
    number: computed(() => topBarBadgeStore.newMessagesCount),
    permissionName: PermissionNames.TICKET_INBOX_SHOW,
  },
  {
    name: "Due Tasks",
    img: "/images/due_tasks.png",
    to: "task-manager/tasks?overdue=true",
    number: computed(() => topBarBadgeStore.overdueTaskCount),
    permissionName: PermissionNames.TASK_SHOW,
  },
];

const smsNumberKey = computed(() => {
  return dialogStore._smsNumber;
});
const handleCloseTasks = () => {
  tasksStore.setCreateModalVisibility(false);
};
const capitalize = function (str: string): string {
  const cleanedRoute = str.replace(/index|pages/g, "");
  return cleanedRoute.replace(/-/g, " ");
};

const postRepeatedActivePing = async function () {
  let timer: NodeJS.Timer;
  const interval = 60;
  if (authStore.isAuthenticated) {
    authStore.postActivePing();
    timer = setInterval(async () => {
      if (!authStore.isAuthenticated) {
        clearInterval(timer);
        return;
      }
      await authStore.postActivePing();

      // Check if client is inactive and redirect, but only if not in call
      const status = await authStore.getClientInfo();
      if (status.client_status === ClientStatus.INACTIVE) {
        handleInactiveClientRedirect();
      }
    }, interval * 1000);

    const status = await authStore.getClientInfo();
    authStore._clientStatus = status.client_status;
    authStore._clientMessage = status.client_message;
    if (status.client_status === ClientStatus.INACTIVE) {
      handleInactiveClientRedirect();
    }
  }
};

const handleInactiveClientRedirect = function () {
  // Check if user is currently in a call
  if (dialogStore.inCall === InCall.ONGOING) {
    // Set up one-time listener for when call ends
    const { $listen, $unListen } = useNuxtApp();
    const redirectAfterCall = () => {
      router.push("/payment-required");
      $unListen("completed", redirectAfterCall);
    };
    $listen("ended", redirectAfterCall);
  } else {
    // No active call, redirect immediately
    router.push("/payment-required");
  }
};

const handleClose = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};
// check if the route is '/help-desk/inbox' using computed
const isHelpDeskInbox = computed(() => {
  return useRoute().path === "/help-desk/inbox";
});

/*
 * Publish the sticky topbar's height as `--topbar-height`.
 *
 * The topbar is `position: sticky; top: 0; z-index: 110`, so it paints over
 * whatever is under it once the document scrolls. Anything that wants to stay
 * visible has to stick BELOW it, and there is no way to say "below it" in CSS
 * without knowing how tall it is — a number that moves with the breakpoint, the
 * font size and whether the red client-message bar is showing (that bar is
 * inside this element, so it is measured too).
 *
 * The inbox's conversation header is the reason this exists; see
 * `.inbox-mobile-header` in projects/help-desk/components/inbox/Main.vue.
 */
const topBarRef = ref<HTMLElement | null>(null);
let topBarObserver: ResizeObserver | null = null;

const publishTopBarHeight = () => {
  const el = topBarRef.value;
  if (!el) return;
  document.documentElement.style.setProperty(
    "--topbar-height",
    `${Math.round(el.getBoundingClientRect().height)}px`,
  );
};

onMounted(() => {
  publishTopBarHeight();
  if (typeof ResizeObserver === "undefined") return;
  topBarObserver = new ResizeObserver(publishTopBarHeight);
  if (topBarRef.value) topBarObserver.observe(topBarRef.value);
});

onUnmounted(() => {
  topBarObserver?.disconnect();
  topBarObserver = null;
});
const routeName = useRoute().name;
// @ts-ignore
document.title = `${capitalize(routeName)} | SmartComm Call Center`;
</script>
<template>
  <div :class="{ dark: theme.getTheme }" class="font-['Lato'] relative">
    <div class="flex">
      <SideNav class="dark:bg-dark-blue hidden sm:block" />
      <!--
        `min-h-screen` is `100vh`, and on a phone `100vh` is the LARGE viewport —
        the height the page would have if the browser's URL bar were hidden. The
        visible viewport is smaller than that whenever the bar is showing, so
        this shell is taller than the screen and the document scrolls by the
        height of the bar even when the page has nothing more to show.

        Everywhere else that is harmless. On the inbox it is not: the shell is
        sized to the visible viewport by JS, the conversation header sits in
        normal flow directly under the sticky topbar, and those ~60 stray pixels
        of scroll are the whole header — scroll once and it disappears behind
        the topbar with no way to tell why. `100dvh` is the viewport as it
        actually is right now, which is the number the JS measured too.
      -->
      <div
        class="relative bg-light-feature dark:bg-dark-feature w-full"
        :class="
          isHelpDeskInbox
            ? 'min-h-[100dvh]'
            : 'min-h-screen pb-[3.9rem] sm:pb-[0rem]'
        "
      >
        <!--Top Navigation Bar goes here-->
        <!--In the top bar there two dynamic named slots which #title and #secondbar which varies from page to page-->
        <!--Second bar is the bottom section of the top bar-->
        <div
          ref="topBarRef"
          class="bg-light-purple sm:bg-white border-b border-[#3B2A821A]/[.1] dark:border-dark-bluegray-border sticky-nav !z-[110] dark:bg-dark-blue pt-5 sm:pt-0"
        >
          <div
            class="flex items-center 2xl:py-5 p-1.5 px-2 sm:px-4 sm:p-4 justify-between"
            :class="{
              'sm:bg-gradient-to-r sm:from-red-500 sm:via-red-600 sm:to-red-500':
                clientMessage,
            }"
          >
            <!-- The title is the only part of this row that may shrink. On a
                 phone the actions beside it can add up to more than the width
                 of the screen, and nothing in the row wraps or scrolls, so
                 without min-w-0 plus truncate here the buttons on the right
                 get pushed off the edge instead. -->
            <div class="left flex items-center justify-center gap-2 min-w-0">
              <h2
                class="2xl:text-2xl text-lg font-bold dark:text-white text-white truncate"
                :class="{ 'sm:text-light-purple': !clientMessage }"
              >
                {{ theme.title }}
              </h2>
            </div>

            <!-- Client notice from the backend (sm+): paints the whole top bar
                 red with the message centered between the title and the
                 actions. On mobile the message renders in its own bar below
                 the header instead. -->
            <div
              v-if="clientMessage"
              class="flex-1 hidden sm:flex justify-center min-w-0 px-2"
            >
              <div
                class="flex items-center gap-2 min-w-0 text-white text-center px-3 py-1 sm:py-1.5 text-xs sm:text-[13px] font-medium tracking-wide"
              >
                <svg
                  class="h-[16px] w-[16px] shrink-0 opacity-90"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path
                    d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46"
                  />
                </svg>
                <!-- Message is a trusted backend value that may contain an <a> link. -->
                <!-- eslint-disable vue/no-v-html -->
                <span
                  ref="clientMessageRef"
                  class="client-message leading-snug"
                  v-html="clientMessage"
                />
                <!-- eslint-enable vue/no-v-html -->
              </div>
            </div>

            <div
              class="space-x-2 items-center text-sm flex shrink-0"
              :class="{ 'client-alert-actions': clientMessage }"
            >
              <NavButton
                v-if="
                  isUserPermitted(PermissionNames.MAKE_CALL) &&
                  dialogStore._showMaximizeButton
                "
                class="rounded-full text-xs"
                @click="
                  () => {
                    dialogStore.setOngoingModalVisibility(true);
                    dialogStore.setMaximizeButtonVisibility(false);
                  }
                "
              >
                {{ formatDuration(dialogStore.callDuration) }}
                <!-- Display formatted duration -->
              </NavButton>

              <!-- Today's follow-ups: the badge count rides along with the
                   other top-bar counts; the list itself is only fetched when
                   the agent opens the modal. -->
              <div v-if="canSeeTodayFollowUps" class="hidden sm:block">
                <el-tooltip
                  class="box-item"
                  effect="dark"
                  content="Today's Followups"
                  placement="bottom"
                >
                  <NavButton
                    class="relative border-[#3B2A8233]/[.2]"
                    @click="openTodayFollowUps"
                  >
                    <svg
                      class="2xl:w-5 2xl:h-5 w-4 h-4 text-[#494b55] dark:text-white"
                      :class="clientMessage ? 'sm:!text-white' : ''"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 2v3M16 2v3M3.5 9.5h17M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v12A1.5 1.5 0 0 1 19 20.5H5A1.5 1.5 0 0 1 3.5 19V7A1.5 1.5 0 0 1 5 5.5Z"
                      />
                      <path d="m9 14.5 2 2 4-4" />
                    </svg>

                    <Badge
                      v-if="topBarBadgeStore.todayFollowUpCount > 0"
                      class="top-[-8px] right-[-10px] absolute"
                    >
                      {{ topBarBadgeStore.todayFollowUpCount }}
                    </Badge>
                  </NavButton>
                </el-tooltip>
              </div>
              <!-- help desk badges start -->
              <div
                v-for="nav in rightNav"
                :key="nav.to"
                class="hidden sm:block"
              >
                <el-tooltip
                  class="box-item"
                  effect="dark"
                  :content="nav.name"
                  placement="bottom"
                >
                  <NavButton
                    v-if="
                      isUserPermitted(nav.permissionName) &&
                      authStore.features.help_desk
                    "
                    class="relative border-[#3B2A8233]/[.2]"
                  >
                    <NuxtLink :to="`/${nav.to}`">
                      <img
                        :src="`${nav.img}`"
                        alt=""
                        class="2xl:w-5 2xl:h-5 w-4 h-4 object-contain dark:filter dark:invert dark:grayscale dark:brightness-250 dark:contrast-200"
                      />

                      <Badge
                        v-if="nav.number.value > 0"
                        class="top-[-8px] right-[-10px] absolute"
                      >
                        {{ nav.number }}
                      </Badge>
                    </NuxtLink>
                  </NavButton>
                </el-tooltip>
              </div>
              <!-- help desk badges end -->
              <!-- SIP connection status: sits right before the online/break
                   status dropdown so the two presence indicators read as one
                   group. -->
              <div v-if="isUserPermitted(PermissionNames.MAKE_CALL)">
                <NavButton class="rounded-full">
                  <el-tooltip
                    :content="useAgentSipConfigStore().connectionMessage"
                  >
                    <div>
                      <Svgs
                        class="cursor-pointer select-none 2xl:w-5 w-4 rotate-[132deg] mt-1 sm:mt-0"
                        fill="currentColor"
                        :class="[
                          getConnectionStatusClass(
                            useAgentSipConfigStore().connectionStatus,
                          ),
                          clientMessage ? 'sm:!text-white' : '',
                        ]"
                        name="solid-phone"
                        @click="handleConnectionClick"
                      />
                    </div>
                  </el-tooltip>
                </NavButton>
              </div>
              <el-tooltip
                class="box-item"
                effect="dark"
                content="Click to change your active status"
                placement="top"
              >
                <el-dropdown trigger="click" @command="handleCommand">
                  <NavButton>
                    <div
                      class="p-[3px] 2xl:p-[4px] dark:border-dark-primary-border"
                    >
                      <div
                        class="p-[5px] 2xl:p-[6px] rounded-full"
                        :class="getUserStatusClass(authStore.user.status)"
                      ></div>
                    </div>
                  </NavButton>
                  <template #dropdown>
                    <div class="max-w-32">
                      <el-dropdown-menu>
                        <el-dropdown-item :key="'online'" command="online">
                          <div class="flex gap-2 items-center">
                            <div class="p-[3px]">
                              <div
                                class="p-[5px] rounded-full"
                                :class="getUserStatusClass('online')"
                              ></div>
                            </div>
                            <span
                              :class="{
                                'font-bold text-light-purple':
                                  'online' === authStore.user.status,
                              }"
                              >Online</span
                            >
                          </div>
                        </el-dropdown-item>
                        <el-dropdown-item
                          v-for="item in configStore.globalConfig
                            .break_statuses"
                          :key="item"
                          :command="item"
                        >
                          <div class="flex gap-2 items-center">
                            <div class="p-[3px]">
                              <div
                                class="p-[5px] rounded-full"
                                :class="getUserStatusClass('offline')"
                              ></div>
                            </div>
                            <span
                              class="capitalize"
                              :class="{
                                'font-bold text-light-purple':
                                  item === authStore.user.break_reason,
                              }"
                            >
                              {{ item }}
                            </span>
                          </div>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </div>
                  </template>
                </el-dropdown>
              </el-tooltip>
              <NavButton
                v-if="isUserPermitted(PermissionNames.SEND_SMS)"
                class="sm:hidden"
                @click="dialogStore.setSmsPopUpVisibility(true)"
              >
                <img src="/svgs/sidebar/sms-histories.svg" alt="" class="w-5" />
              </NavButton>
              <NavButton
                v-if="isUserPermitted(PermissionNames.MAKE_CALL)"
                class="sm:hidden"
                @click="dialogStore.setDialerVisibility(true)"
              >
                <Svgs name="solid-phone" class="text-white w-[18px]" />
              </NavButton>
              <!-- What's New: the megaphone sits beside the bell on purpose.
                   Both are "something happened while you were working" counters,
                   so an agent only has one place to look. The follow-ups and
                   help desk buttons are hidden below sm; this one deliberately
                   is not, so updates are reachable from the bar itself at every
                   width and not only through the support widget's floating
                   launcher or the dialog that opens by itself. The page title
                   to the left truncates so this extra button cannot push the
                   phone row off the screen. The badge counts unread
                   announcements only — task updates are deliberately not part
                   of this number, because the What's New page does not list
                   them either. -->
              <el-tooltip
                class="box-item"
                effect="dark"
                content="What's New"
                placement="bottom"
              >
                <NavButton
                  class="relative border-[#3B2A8233]/[.2]"
                  aria-label="What's New"
                  @click="router.push('/whats-new')"
                >
                  <!-- The icon is white below sm, where it sits on the coloured
                       header bar, and takes the neighbours' grey from sm up.
                       Dark mode wins over the breakpoint on specificity, so the
                       icon stays white there at every width. -->
                  <svg
                    class="2xl:w-5 2xl:h-5 w-4 h-4 text-white sm:text-[#494b55] dark:text-white"
                    :class="clientMessage ? 'sm:!text-white' : ''"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <!-- A bullhorn, not a loudspeaker. This bar already carries
                         two phone glyphs and a SIP indicator, so a speaker with
                         sound waves would read as volume or mute rather than as
                         announcements. The group is tilted because that is how a
                         megaphone is normally drawn: the horn is the cone, the
                         loop under it is the handle, and the arc off the mouth is
                         the sound coming out. -->
                    <g transform="rotate(-18 12 12)">
                      <path d="M3.5 10.5 18.5 5.5v13l-15-5v-3Z" />
                      <path d="M6 14.3v2.2a2.4 2.4 0 0 0 4.8 0v-.6" />
                      <path d="M20.5 9.5a4.5 4.5 0 0 1 0 5" />
                    </g>
                  </svg>

                  <Badge
                    v-if="supportStore.announcementsUnread > 0"
                    class="top-[-8px] right-[-10px] absolute"
                  >
                    {{ supportStore.announcementsUnread }}
                  </Badge>
                </NavButton>
              </el-tooltip>
              <NotificationPopUp />

              <Svgs
                v-if="isHelpDeskInbox"
                name="menu"
                class="text-white w-[25px] sm:hidden block"
                @click="globalComponentStore.setShowMoreModalVisibility(true)"
              />
              <div ref="profileTabButton" class="relative grid">
                <button
                  class="dark:border-[#303755] rounded-full"
                  @click="profileTab = !profileTab"
                >
                  <div class="hidden sm:block">
                    <div
                      class="w-[2rem] h-[2rem] overflow-hidden rounded-full dark:border-dark-primary-border"
                    >
                      <img
                        :src="
                          authStore.user?.profile_picture_public_url ||
                          '/images/avatar.png'
                        "
                        alt="Profile Picture"
                        class="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <Svgs
                    name="hr-ellipsis"
                    class="text-white w-[25px] sm:hidden"
                  />
                </button>
                <TransWrap :show="profileTab as boolean">
                  <PopUp v-show="profileTab">
                    <!-- The menu sets its own width. PopUp is `w-max`, so left
                         to itself the panel is exactly as wide as its longest
                         line and changes width from one account to the next. -->
                    <div
                      class="w-[16rem] max-w-[calc(100vw-1.5rem)] p-1.5 text-black dark:text-light-font"
                    >
                      <!-- Whose account this is. The photo when there is one,
                           initials when there is not: the generic head glyph
                           that used to sit here answered none of that, and it
                           is the one question this block exists to answer. -->
                      <div class="flex items-center gap-3 px-2.5 py-2.5">
                        <img
                          v-if="authStore.user?.profile_picture_public_url"
                          :src="authStore.user.profile_picture_public_url"
                          alt=""
                          class="h-10 w-10 shrink-0 rounded-full object-cover"
                        />
                        <span
                          v-else
                          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ECEBFB] text-[13px] font-semibold uppercase text-light-sec-purple dark:bg-[#35336b] dark:text-white"
                        >
                          {{ profileInitials }}
                        </span>
                        <div class="min-w-0 flex-1">
                          <p
                            class="truncate text-[13px] font-semibold leading-tight"
                          >
                            {{ profileName || "Signed in" }}
                          </p>
                          <!-- Rendered only when we actually have one, so a
                               half-loaded session shows a name with nothing
                               under it rather than the word "undefined". -->
                          <p
                            v-if="profileEmail"
                            class="mt-0.5 truncate text-[11px] leading-tight text-light-text-purple dark:text-dark-text-blue"
                          >
                            {{ profileEmail }}
                          </p>
                        </div>
                      </div>

                      <div
                        class="my-1 h-px bg-black/[.07] dark:bg-light-purple-bar/[.4]"
                      />

                      <div class="px-2.5 pb-1 pt-2">
                        <p
                          id="profile-theme-label"
                          class="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-light-text-purple dark:text-dark-text-blue"
                        >
                          Theme
                        </p>
                        <!-- Three buttons, not the switch that used to be here.
                             A two-position switch can only say light or dark,
                             and the setting now has a third value — System,
                             which follows the machine and is the default. A
                             switch also never says which side is currently on;
                             `aria-pressed` buttons say so to everyone.

                             Hand-written rather than <HomeSegmented>: that one
                             is a 32px-tall row of text labels built for a
                             filter bar, and this needs a phone-sized tap target
                             and an icon per option to stay readable at 11px. -->
                        <div
                          role="group"
                          aria-labelledby="profile-theme-label"
                          class="grid grid-cols-3 gap-1 rounded-lg bg-black/[.04] p-1 dark:bg-white/[.06]"
                        >
                          <button
                            v-for="option in themeOptions"
                            :key="option.value"
                            type="button"
                            :aria-pressed="theme.themeMode === option.value"
                            :class="[
                              'flex h-[52px] flex-col items-center justify-center gap-1 rounded-md text-[11px] font-medium leading-none transition-colors',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-light-sec-purple focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-dark-blue',
                              theme.themeMode === option.value
                                ? 'bg-white text-light-purple shadow-sm dark:bg-light-purple-bar dark:text-white'
                                : 'text-light-text-purple hover:bg-black/[.04] dark:text-dark-text-blue dark:hover:bg-white/[.08]',
                            ]"
                            @click="theme.setThemeMode(option.value)"
                          >
                            <svg
                              class="h-[18px] w-[18px]"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              aria-hidden="true"
                            >
                              <path
                                :d="option.icon"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                            {{ option.label }}
                          </button>
                        </div>
                      </div>

                      <div
                        class="my-1 h-px bg-black/[.07] dark:bg-light-purple-bar/[.4]"
                      />

                      <NuxtLink
                        class="flex h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px] transition-colors hover:bg-black/[.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-light-sec-purple focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:hover:bg-white/[.06] dark:focus-visible:ring-offset-dark-blue"
                        to="/profile"
                      >
                        <svg
                          class="h-[18px] w-[18px] shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          aria-hidden="true"
                        >
                          <path
                            d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        My Profile
                      </NuxtLink>

                      <!-- Red, but only as its own colour: a filled red button
                           in a four-item menu shouts, and signing out is a
                           routine thing to do at the end of a shift. -->
                      <button
                        type="button"
                        class="flex h-10 w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px] text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:text-red-400 dark:hover:bg-red-500/[.12] dark:focus-visible:ring-offset-dark-blue"
                        @click="signOut"
                      >
                        <svg
                          class="h-[18px] w-[18px] shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="1.5"
                          aria-hidden="true"
                        >
                          <path
                            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </PopUp>
                </TransWrap>
              </div>
            </div>
          </div>

          <!-- Mobile (below sm): the header keeps its normal color and the
               client message gets its own red bar right under it. -->
          <div
            v-if="clientMessage"
            class="sm:hidden flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 via-red-600 to-red-500 px-3 py-2 text-center text-xs font-medium tracking-wide text-white"
          >
            <svg
              class="h-[16px] w-[16px] shrink-0 opacity-90"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 1 1 0-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 0 1-1.44-4.282m3.102.069a18.03 18.03 0 0 1-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 0 1 8.835 2.535M10.34 6.66a23.847 23.847 0 0 0 8.835-2.535m0 0A23.74 23.74 0 0 0 18.795 3m.38 1.125a23.91 23.91 0 0 1 1.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 0 0 1.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 0 1 0 3.46"
              />
            </svg>
            <!-- Message is a trusted backend value that may contain an <a> link. -->
            <!-- eslint-disable vue/no-v-html -->
            <span
              ref="clientMessageMobileRef"
              class="client-message leading-snug"
              v-html="clientMessage"
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
        <div class="feature-slot">
          <!--This is the dynamic part of the page that keeps changin i.e:feature-card-->
          <slot />
          <button
            v-if="
              isUserPermitted(PermissionNames.MAKE_CALL) &&
              !dialogStore.showDialer
            "
            class="dialer-button dark:placeholder-placeholder-white"
            @click="dialogStore.setDialerVisibility(true)"
          >
            Dialer
          </button>
          <button
            v-if="
              isUserPermitted(PermissionNames.SEND_SMS) &&
              !dialogStore._isSmsPopUpVisible
            "
            class="sms-button"
            @click="dialogStore.setSmsPopUpVisibility(true)"
          >
            SMS
          </button>
          <!-- <el-dialog
          v-if="
            authStore.features.order_manager && ordersStore.showOrderCreateModal
          "
          v-model="ordersStore.showOrderCreateModal"
          width="80%"
          align-center
          top="0"
        >
          <template #header="{ titleId, titleClass }">
            <div class="my-header flex flex-col gap-1">
              <h4 :id="titleId" class="text-md" :class="titleClass">
                Create Order
              </h4>
            </div>
          </template>
          <div class="subtext-dialog">
            <OrdersCreateForm
              v-if="ordersStore.showOrderCreateModal"
              :show-header="false"
              @close-modal="ordersStore.setCreateModalVisibility(false)"
            ></OrdersCreateForm>
          </div>
        </el-dialog> -->
          <AgentsChatBox />
          <AgentsUpdatesModal />
          <el-dialog
            v-if="authStore.features.task_manager && tasksStore.showCreateModal"
            v-model="tasksStore.showCreateModal"
            :modal="false"
            width="65%"
            align-center
            draggable
            top="0"
            class=""
            @close="handleClose"
          >
            <template #header="{ titleId, titleClass }">
              <div class="my-header flex flex-col gap-1">
                <h4 :id="titleId" class="text-md" :class="titleClass">
                  Create Task
                </h4>
              </div>
            </template>
            <div class="subtext-dialog">
              <TasksCreateForm
                ref="formRef"
                :show-header="false"
              ></TasksCreateForm>
            </div>
          </el-dialog>
          <el-dialog
            v-if="
              isUserPermitted(PermissionNames.LEAD_CREATE) &&
              authStore.features.crm
            "
            v-model="leadsStore.showCreateDialog"
            width="80%"
            align-center
            @close="leadsStore.closeCreateDialog"
          >
            <template #header="{ titleId, titleClass }">
              <div class="my-header flex flex-col gap-1">
                <h4 :id="titleId" class="text-md" :class="titleClass">
                  Create Lead
                </h4>
              </div>
            </template>
            <div class="subtext-dialog">
              <LeadsCreateForm
                :key="`lead-form-${leadsStore.showCreateDialog}-${dialogStore.currentProfile?.id}`"
                :profile-id="dialogStore.currentProfile?.id"
                :profile-name="dialogStore.currentProfile?.name"
              />
            </div>
          </el-dialog>
          <LeadsTodayFollowUpsModal
            v-if="canSeeTodayFollowUps"
            v-model="leadsStore.showTodayFollowUpsModal"
          />
          <el-dialog v-model="profileStore.showFraudCheckerModal">
            <template #header="{ titleId, titleClass }">
              <div class="my-header flex flex-col gap-1">
                <h4 :id="titleId" class="text-md" :class="titleClass">
                  Fraud Check of {{ profileStore.fraudCheckerPhone }}
                </h4>
              </div>
            </template>
            <InboxFraudCheckerTable
              :remote-table-data="profileStore._fraudCheckerData"
            />
          </el-dialog>
          <CallRecordingDialog />
          <CallDialer
            :show-edit-modal="dialogStore.showDialer"
            :phone-no="dialerNumber"
            @close="dialogStore.setDialerVisibility(false)"
          />
          <EditModal
            v-if="isUserPermitted(PermissionNames.OPPORTUNITY_CREATE)"
            :show-edit-modal="opportunitiesStore.showOpportunityCreateModal"
            @close="opportunitiesStore.closeOpportunityCreateModal"
          >
            <OpportunitiesCreateForm
              :key="opportunitiesStore.selectedLeadData?.id"
            />
          </EditModal>

          <SmsPopUp :key="smsNumberKey"></SmsPopUp>
          <CallAgentList></CallAgentList>
          <ShortcutList />

          <div class="call">
            <client-only>
              <CallIncoming></CallIncoming>
            </client-only>
          </div>

          <div class="oncall">
            <client-only>
              <CallOngoing></CallOngoing>
            </client-only>
          </div>

          <div class="warning">
            <client-only>
              <CallWarning></CallWarning>
            </client-only>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isHelpDeskInbox"
      class="fixed bottom-0 bg-white py-3 w-full z-[50] sm:hidden px-5 flex justify-between border-t border-gray-200 dark:border-light-purple-bar/[.5] dark:bg-dark-blue"
    >
      <NuxtLink to="/home" class="flex flex-col items-center dark:text-white">
        <Svgs class="w-5" name="dashboard" />
        <span class="text-xs">Home</span>
      </NuxtLink>
      <NuxtLink
        v-if="isUserPermitted(PermissionNames.TICKET_SHOW)"
        to="/help-desk/inbox"
        class="flex flex-col items-center dark:text-white"
      >
        <Svgs class="w-5" name="inbox" />
        <span class="text-xs">Inbox</span>
      </NuxtLink>
      <NuxtLink
        v-if="isUserPermitted(PermissionNames.TICKET_SHOW)"
        to="/help-desk/ticket-list"
        class="flex flex-col items-center dark:text-white"
      >
        <svg
          stroke="currentColor"
          width="22"
          stroke-width=".2"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          class="text-black"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_2036_2125)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.38587 12.0151C0.84031 12.0609 0.352125 11.6633 0.344258 11.1159C0.315649 9.12519 0.869319 7.17125 1.95152 5.48399C3.21123 3.51997 5.11626 2.02769 7.36952 1.23987C9.62277 0.452052 12.0976 0.412986 14.408 1.12876C16.7184 1.84454 18.7346 3.27493 20.142 5.19685C21.5495 7.11877 22.2691 9.42417 22.1887 11.7535C22.1454 13.0066 21.8719 14.2288 21.3895 15.366C21.2031 15.8053 21.3116 16.5904 21.4229 17.3957C21.5591 18.3809 21.6995 19.3963 21.3093 19.846C20.9226 20.2916 20.0338 20.1898 19.1489 20.0885C18.3879 20.0014 17.63 19.9146 17.1969 20.1765C16.1343 20.8191 14.9534 21.2804 13.7036 21.5319C11.6743 21.9402 9.56179 21.7757 7.61292 21.068C7.10083 20.882 6.89104 20.2885 7.12716 19.7975C7.35902 19.3154 7.93416 19.1146 8.44004 19.2886C9.98585 19.8203 11.6509 19.9359 13.2522 19.6137C15.1677 19.2282 16.8845 18.2379 18.1349 16.797C19.3852 15.3561 20.0987 13.5457 20.1642 11.6482C20.2298 9.75067 19.6435 7.87267 18.497 6.30706C17.3505 4.74145 15.7081 3.57624 13.8261 2.99316C11.944 2.41008 9.92797 2.44191 8.09244 3.08367C6.25692 3.72543 4.70507 4.94106 3.6789 6.54096C2.84013 7.8487 2.39111 9.35309 2.36957 10.8928C2.36161 11.4611 1.95227 11.9675 1.38587 12.0151ZM4.52814 11.808C4.03625 11.8531 3.59161 11.4974 3.59447 11.0034C3.60214 9.67892 3.9781 8.38186 4.69092 7.25153C5.56215 5.87 6.88762 4.81521 8.46062 4.25166C10.0336 3.68811 11.7657 3.64749 13.3868 4.13612C15.0079 4.62475 16.4268 5.61517 17.4223 6.95292C18.4178 8.29067 18.9339 9.90055 18.8901 11.5315C18.8463 13.1624 18.2451 14.7228 17.1802 15.9691C16.1154 17.2155 14.6467 18.0778 13.0032 18.4217C11.6503 18.7047 10.2414 18.6233 8.92699 18.1942C8.45971 18.0416 8.26634 17.5064 8.47598 17.0618C8.68183 16.6252 9.19944 16.4421 9.66276 16.5774C10.6104 16.8541 11.6153 16.8966 12.5828 16.6942C13.8335 16.4325 14.9512 15.7762 15.7617 14.8277C16.5721 13.8791 17.0296 12.6917 17.063 11.4504C17.0963 10.2092 16.7035 8.98403 15.9459 7.96594C15.1883 6.94784 14.1084 6.19409 12.8747 5.82222C11.641 5.45034 10.3227 5.48126 9.12561 5.91015C7.92847 6.33904 6.91973 7.14178 6.25668 8.1932C5.76098 8.97924 5.47933 9.87122 5.42998 10.7883C5.40243 11.3004 5.03882 11.7612 4.52814 11.808ZM12.7312 11.4122C13.5529 11.4122 14.2191 10.746 14.2191 9.92429C14.2191 9.10253 13.5529 8.43636 12.7312 8.43636C11.9094 8.43636 11.2432 9.10253 11.2432 9.92429C11.2432 10.746 11.9094 11.4122 12.7312 11.4122Z"
              fill="#676B87"
            />
          </g>
          <defs>
            <clipPath id="clip0_2036_2125">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>

        <span class="text-xs">Tickets</span>
      </NuxtLink>
      <NuxtLink
        v-if="isUserPermitted(PermissionNames.TASK_SHOW)"
        to="/task-manager/tasks"
        class="flex flex-col items-center dark:text-white"
      >
        <Svgs class="w-5" name="task" />
        <span class="text-xs">Task</span>
      </NuxtLink>
      <NuxtLink
        class="flex flex-col items-center dark:text-white cursor-pointer"
        @click="globalComponentStore.setShowMoreModalVisibility(true)"
      >
        <Svgs class="w-5" name="menu" />
        <span class="text-xs">More</span>
      </NuxtLink>
    </div>
    <EditModal
      hide-mask="sm:hidden"
      :show-edit-modal="globalComponentStore._showMoreModal"
      bg-color="bg-light-purple "
      @close="globalComponentStore.setShowMoreModalVisibility(false)"
    >
      <SideNav class="h-[80vh]" />
    </EditModal>
    <ProfileMergeModal :key="profileStore.selectedProfileForEdit?.id" />
  </div>
</template>
<style scoped>
/* Links coming from the v-html client message need :deep to be styled. */
.client-message :deep(a) {
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: opacity 0.15s ease;
}
.client-message :deep(a:hover) {
  opacity: 0.85;
}

/* While the red client-message bar is shown (sm+ only — on mobile the header
   keeps its normal color), the top-bar trigger buttons keep their default bg
   and gain a white border so they stay visible on red, filling red-500 on
   hover. Only the first two levels are targeted so buttons inside the
   dropdown and pop-up panels (notifications, profile menu) keep their own
   styling. */
@media (min-width: 640px) {
  .client-alert-actions :deep(> button),
  .client-alert-actions :deep(> div > button) {
    border: 1px solid #fff;
    color: #fff;
    transition: background-color 0.2s ease;
  }
  .client-alert-actions :deep(> button:hover),
  .client-alert-actions :deep(> div > button:hover) {
    background-color: #ef4444; /* red-500 */
    color: #fff;
  }
  /* Status-colored icons (e.g. the red "disconnected" phone) would vanish on
     the red-500 hover bg, so hovered buttons render their icons white. */
  .client-alert-actions :deep(> button:hover svg),
  .client-alert-actions :deep(> div > button:hover svg) {
    color: #fff;
  }
}

.router-link-exact-active {
  color: #3b2a82 !important;
}
.router-link-exact-active > svg {
  color: #3b2a82 !important;
}
</style>
